/**
 * Chat v2 module - Vercel AI SDK powered chat interface
 *
 * Feature-flagged chat with streaming responses and inline data visualizations.
 * Smart routing based on environment and debug flag:
 * - Production builds: always use ChatV2Production
 * - Development builds: ChatV2Debug if REACT_APP_CHAT_V2_DEBUG_UI=1, else ChatV2Production
 */

import { ConversationProvider } from '@elevenlabs/react';
import { useChatV2DebugUI } from '../hooks/useChatV2Enabled';
import { ChatV2Debug } from './ChatV2Debug';
import { ChatV2Production } from './ChatV2Production';

interface ChatV2Props {
  isDesktop?: boolean;
  onClose?: () => void;
  expanded?: boolean;
  toggleExpanded?: () => void;
}

/**
 * Smart Chat v2 Router
 *
 * Routes to the appropriate UI based on environment and feature flags:
 * - Production: Always uses polished production UI
 * - Development with debug flag: Uses debug UI with technical features
 * - Development without debug flag: Uses production UI
 */
export function ChatV2(props: ChatV2Props) {
  const showDebugUI = useChatV2DebugUI();

  // Production always uses the polished UI; dev honors the debug flag.
  const useDebug = process.env.NODE_ENV !== 'production' && showDebugUI;

  // ConversationProvider powers the in-chat "Talk" button (ChatTalkButton,
  // rendered in the input bar). Harmless when the chatVoice flag is off — no
  // session is ever started.
  return (
    <ConversationProvider>
      {useDebug ? <ChatV2Debug {...props} /> : <ChatV2Production {...props} />}
    </ConversationProvider>
  );
}

// Also export individual variants for direct imports if needed
export { ChatV2Debug } from './ChatV2Debug';
export { ChatV2Production } from './ChatV2Production';
